// Frontend OpenTelemetry instrumentation.
//
// Deliberately scoped: document load, fetch/XHR, and important user
// interactions. Every span carries `session.id` (from the telemetry session
// manager). W3C trace headers are only propagated to allowlisted API origins,
// so trace context is never leaked to third-party hosts. All telemetry is
// exported to the same-origin `/telemetry` gateway (which validates and
// forwards to the OTel Collector); export failures are swallowed by the SDK.

import '@opentelemetry/context-zone'
import { context, propagation, SpanStatusCode, trace } from '@opentelemetry/api'
import { CompositePropagator, W3CBaggagePropagator, W3CTraceContextPropagator } from '@opentelemetry/core'
import { OTLPTraceExporter } from '@opentelemetry/exporter-trace-otlp-http'
import { registerInstrumentations } from '@opentelemetry/instrumentation'
import { DocumentLoadInstrumentation } from '@opentelemetry/instrumentation-document-load'
import { FetchInstrumentation } from '@opentelemetry/instrumentation-fetch'
import { UserInteractionInstrumentation } from '@opentelemetry/instrumentation-user-interaction'
import { XMLHttpRequestInstrumentation } from '@opentelemetry/instrumentation-xml-http-request'
import { resourceFromAttributes } from '@opentelemetry/resources'
import {
  AlwaysOnSampler,
  BatchSpanProcessor,
  TraceIdRatioBasedSampler,
  WebTracerProvider,
  type Span,
  type SpanProcessor,
} from '@opentelemetry/sdk-trace-web'
import {
  ATTR_DEPLOYMENT_ENVIRONMENT_NAME,
  ATTR_SERVICE_NAME,
  ATTR_SERVICE_VERSION,
} from '@opentelemetry/semantic-conventions'

import { resolveApiClientBaseUrl } from '@lib/runtime-url'
import { getOrCreateTelemetrySession } from '@lib/telemetrySession'

let initialized = false

// ---------------------------------------------------------------------------
// Configuration (env-driven; never hard-code environment-specific values)
// ---------------------------------------------------------------------------

const PUBLIC_OTEL_SERVICE_NAME =
  (import.meta.env.VITE_PUBLIC_OTEL_SERVICE_NAME as string | undefined) || 'hms-web'
const APP_VERSION = (import.meta.env.VITE_PUBLIC_APP_VERSION as string | undefined) || 'dev'
const DEPLOYMENT_ENVIRONMENT =
  (import.meta.env.VITE_PUBLIC_DEPLOYMENT_ENVIRONMENT as string | undefined) || 'development'
// Same-origin gateway that validates and forwards OTLP to the collector.
const TELEMETRY_ENDPOINT =
  (import.meta.env.VITE_PUBLIC_TELEMETRY_ENDPOINT as string | undefined) || '/telemetry'

function telemetryExporterUrl(): string {
  const base = TELEMETRY_ENDPOINT.replace(/\/$/, '')
  return `${base}/v1/traces`
}

/** Origins that are allowed to receive W3C trace headers (CORS propagation). */
function telemetryApiOrigins(): Array<string | RegExp> {
  const origins: Array<string | RegExp> = [window.location.origin]
  try {
    const apiOrigin = new URL(resolveApiClientBaseUrl(import.meta.env.VITE_API_URL)).origin
    origins.push(apiOrigin)
  } catch {
    // no explicit API origin — same-origin only
  }
  return origins
}

function isEnabled(): boolean {
  const flag = import.meta.env.VITE_OTEL_ENABLED as string | undefined
  return flag !== 'false'
}

// ---------------------------------------------------------------------------
// Span processor: attach session.id to every span
// ---------------------------------------------------------------------------

class SessionIdSpanProcessor implements SpanProcessor {
  onStart(span: Span): void {
    const sessionId = getOrCreateTelemetrySession().id
    if (sessionId) span.setAttribute('session.id', sessionId)
  }

  onEnd(): void {
    /* no-op */
  }

  shutdown(): Promise<void> {
    return Promise.resolve()
  }

  forceFlush(): Promise<void> {
    return Promise.resolve()
  }
}

// ---------------------------------------------------------------------------
// Public setup
// ---------------------------------------------------------------------------

export function setupTelemetry(): void {
  if (initialized || !isEnabled()) return
  initialized = true

  const resource = resourceFromAttributes({
    [ATTR_SERVICE_NAME]: PUBLIC_OTEL_SERVICE_NAME,
    [ATTR_SERVICE_VERSION]: APP_VERSION,
    [ATTR_DEPLOYMENT_ENVIRONMENT_NAME]: DEPLOYMENT_ENVIRONMENT,
  })

  const ratio = Number(import.meta.env.VITE_OTEL_SAMPLER_ARG ?? '1')
  const sampler = !Number.isFinite(ratio) || ratio >= 1 ? new AlwaysOnSampler() : new TraceIdRatioBasedSampler(ratio)

  const provider = new WebTracerProvider({
    resource,
    sampler,
    spanProcessors: [
      new SessionIdSpanProcessor(),
      new BatchSpanProcessor(new OTLPTraceExporter({ url: telemetryExporterUrl(), timeoutMillis: 5000 })),
    ],
  })
  provider.register()

  propagation.setGlobalPropagator(
    new CompositePropagator({
      propagators: [new W3CTraceContextPropagator(), new W3CBaggagePropagator()],
    }),
  )

  const corsUrls = telemetryApiOrigins()

  registerInstrumentations({
    instrumentations: [
      new DocumentLoadInstrumentation(),
      new FetchInstrumentation({ propagateTraceHeaderCorsUrls: corsUrls }),
      new XMLHttpRequestInstrumentation({ propagateTraceHeaderCorsUrls: corsUrls }),
      new UserInteractionInstrumentation(),
    ],
  })
}

export function isTelemetryEnabled(): boolean {
  return isEnabled()
}
// ---------------------------------------------------------------------------
// Bugsink (Sentry-compatible) error tracking — errors only.
// Tracing is owned by OpenTelemetry; Sentry's tracesSampleRate stays 0. Errors
// are enriched with the active OTel trace and the session id. The browser never
// sets a user identity (that is derived server-side from the JWT).
// ---------------------------------------------------------------------------

export function setupErrorTracking(): void {
  const dsn = import.meta.env.VITE_SENTRY_DSN as string | undefined
  if (!dsn) return

  import('@sentry/react').then((Sentry) => {
    Sentry.init({
      dsn,
      environment: DEPLOYMENT_ENVIRONMENT,
      release: APP_VERSION,
      tracesSampleRate: 0, // errors only
      sendDefaultPii: false,
      tunnel: '/api/sentry-tunnel',
      beforeSend(event) {
        const active = trace.getActiveSpan()
        const ctx = active?.spanContext()
        if (ctx && ctx.traceId && ctx.spanId) {
          event.contexts = {
            ...event.contexts,
            trace: {
              trace_id: ctx.traceId,
              span_id: ctx.spanId,
            },
          }
          event.tags = { ...event.tags, trace_id: ctx.traceId }
        }
        const sessionId = getOrCreateTelemetrySession().id
        if (sessionId) event.tags = { ...event.tags, session_id: sessionId }
        return event
      },
    })
  })
}

export { trace, context, SpanStatusCode }
